import React, { useEffect, useMemo, useState } from "react";
import { getAICostBreakdown, getAIInteractions } from "../api/client.js";

function formatDate(value) {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
}

function formatMoney(value) {
  const amount = Number(value || 0);
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2
  }).format(amount);
}

function formatDateInput(date) {
  if (!date) return "";
  return new Date(date).toISOString().slice(0, 10);
}

export default function CrmAiLogsPage() {
  const [interactions, setInteractions] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 50, total: 0, pages: 1 });
  const [loadingInteractions, setLoadingInteractions] = useState(true);
  const [interactionError, setInteractionError] = useState("");
  const [filters, setFilters] = useState({ feature: "", page: 1, pageSize: 50 });

  const [costBreakdown, setCostBreakdown] = useState(null);
  const [loadingCost, setLoadingCost] = useState(true);
  const [costError, setCostError] = useState("");
  const [dateRange, setDateRange] = useState(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 30);
    return { start, end };
  });

  useEffect(() => {
    let mounted = true;
    setLoadingInteractions(true);
    setInteractionError("");
    getAIInteractions(filters)
      .then((response) => {
        if (!mounted) return;
        setInteractions(Array.isArray(response?.interactions) ? response.interactions : []);
        setPagination(
          response?.pagination || {
            page: filters.page,
            pageSize: filters.pageSize,
            total: 0,
            pages: 1
          }
        );
      })
      .catch((err) => setInteractionError(err.message || "Unable to load AI interactions"))
      .finally(() => {
        if (mounted) setLoadingInteractions(false);
      });

    return () => {
      mounted = false;
    };
  }, [filters]);

  useEffect(() => {
    let mounted = true;
    setLoadingCost(true);
    setCostError("");
    getAICostBreakdown({
      startDate: dateRange.start.toISOString(),
      endDate: dateRange.end.toISOString()
    })
      .then((response) => {
        if (!mounted) return;
        setCostBreakdown(response || null);
      })
      .catch((err) => setCostError(err.message || "Unable to load AI cost breakdown"))
      .finally(() => {
        if (mounted) setLoadingCost(false);
      });

    return () => {
      mounted = false;
    };
  }, [dateRange]);

  const featureRows = useMemo(() => {
    if (!costBreakdown?.byFeature) return [];
    return Object.entries(costBreakdown.byFeature).sort((a, b) => b[1] - a[1]);
  }, [costBreakdown]);

  const modelRows = useMemo(() => {
    if (!costBreakdown?.byModel) return [];
    return Object.entries(costBreakdown.byModel).sort((a, b) => b[1] - a[1]);
  }, [costBreakdown]);

  const updateFilters = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value, page: 1 }));
  };

  return (
    <div className="space-y-6">
      <header className="rounded-3xl border border-white/10 bg-white/5 p-6">
        <p className="text-xs uppercase tracking-[0.3em] text-slate-400">AI governance</p>
        <h1 className="text-3xl font-semibold text-white">AI Logs & Cost Controls</h1>
        <p className="text-sm text-white/70">
          Monitor AI usage, cost allocation, and production interactions in real time.
        </p>
      </header>

      <section className="rounded-3xl border border-white/10 bg-white/5 p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Cost breakdown</p>
            <h2 className="text-xl font-semibold text-white">AI spend overview</h2>
            <p className="text-xs text-white/60 mt-1">Costs shown in USD based on token usage</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <label className="text-xs text-white/70">
              Start date
              <input
                type="date"
                className="mt-2 w-full rounded-xl border border-white/15 bg-black/40 px-3 py-2 text-xs text-white"
                value={formatDateInput(dateRange.start)}
                onChange={(event) =>
                  setDateRange((prev) => ({ ...prev, start: new Date(event.target.value) }))
                }
              />
            </label>
            <label className="text-xs text-white/70">
              End date
              <input
                type="date"
                className="mt-2 w-full rounded-xl border border-white/15 bg-black/40 px-3 py-2 text-xs text-white"
                value={formatDateInput(dateRange.end)}
                onChange={(event) =>
                  setDateRange((prev) => ({ ...prev, end: new Date(event.target.value) }))
                }
              />
            </label>
          </div>
        </div>

        {costError && (
          <div className="mt-4 rounded-2xl border border-rose-500/40 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">
            {costError}
          </div>
        )}

        {loadingCost ? (
          <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 p-4 text-sm text-white/60">
            Loading AI cost breakdown…
          </div>
        ) : costBreakdown ? (
          <div className="mt-5 grid gap-4 md:grid-cols-3">
            <div className="rounded-2xl border border-white/10 bg-black/40 p-4">
              <p className="text-xs uppercase tracking-wide text-white/60">Total cost</p>
              <p className="text-2xl font-semibold text-white mt-2">{formatMoney(costBreakdown.totalCost)}</p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-black/40 p-4">
              <p className="text-xs uppercase tracking-wide text-white/60">Total interactions</p>
              <p className="text-2xl font-semibold text-white mt-2">
                {costBreakdown.totalInteractions ?? 0}
              </p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-black/40 p-4">
              <p className="text-xs uppercase tracking-wide text-white/60">Period</p>
              <p className="text-sm text-white mt-2">
                {formatDate(costBreakdown.period?.start)} → {formatDate(costBreakdown.period?.end)}
              </p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-black/40 p-4 md:col-span-2">
              <p className="text-xs uppercase tracking-wide text-white/60">Cost by feature</p>
              <div className="mt-3 space-y-2">
                {featureRows.length === 0 ? (
                  <p className="text-xs text-white/60">No feature spend recorded.</p>
                ) : (
                  featureRows.map(([feature, cost]) => (
                    <div key={feature} className="flex items-center justify-between text-sm text-white">
                      <span>{feature}</span>
                      <span className="text-white/70">{formatMoney(cost)}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
            <div className="rounded-2xl border border-white/10 bg-black/40 p-4">
              <p className="text-xs uppercase tracking-wide text-white/60">Cost by model</p>
              <div className="mt-3 space-y-2">
                {modelRows.length === 0 ? (
                  <p className="text-xs text-white/60">No model spend recorded.</p>
                ) : (
                  modelRows.map(([model, cost]) => (
                    <div key={model} className="flex items-center justify-between text-sm text-white">
                      <span>{model}</span>
                      <span className="text-white/70">{formatMoney(cost)}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 p-4 text-sm text-white/60">
            No cost data available for this period.
          </div>
        )}
      </section>

      <section className="rounded-3xl border border-white/10 bg-white/5 p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Interaction logs</p>
            <h2 className="text-xl font-semibold text-white">AI activity feed</h2>
            <p className="text-xs text-white/60 mt-1">Review interactions across features and users.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <label className="text-xs text-white/70">
              Feature
              <input
                className="mt-2 w-full rounded-xl border border-white/15 bg-black/40 px-3 py-2 text-xs text-white"
                value={filters.feature}
                onChange={(event) => updateFilters("feature", event.target.value)}
                placeholder="chat, recommendations, valuation"
              />
            </label>
          </div>
        </div>

        {interactionError && (
          <div className="mt-4 rounded-2xl border border-rose-500/40 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">
            {interactionError}
          </div>
        )}

        {loadingInteractions ? (
          <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 p-4 text-sm text-white/60">
            Loading AI interactions…
          </div>
        ) : interactions.length === 0 ? (
          <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 p-4 text-sm text-white/60">
            No interactions match your filters.
          </div>
        ) : (
          <div className="mt-5 space-y-3">
            {interactions.map((interaction) => (
              <div
                key={interaction.id}
                className="rounded-2xl border border-white/10 bg-black/40 p-4 flex flex-wrap items-start gap-4"
              >
                <div className="min-w-[140px]">
                  <p className="text-xs uppercase tracking-wide text-white/60">Feature</p>
                  <p className="text-sm text-white font-medium mt-1">{interaction.feature}</p>
                  <p className="text-xs text-white/50 mt-2">{formatDate(interaction.createdAt)}</p>
                </div>
                <div className="flex-1 min-w-[200px]">
                  <div className="flex flex-wrap gap-2 text-xs text-white/60">
                    <span className="rounded-full border border-white/15 px-2 py-1">
                      Model: {interaction.model}
                    </span>
                    <span className="rounded-full border border-white/15 px-2 py-1">
                      Prompt: {interaction.promptVersion || "—"}
                    </span>
                    {interaction.wasEscalated && (
                      <span className="rounded-full border border-amber-400/40 bg-amber-500/10 px-2 py-1 text-amber-200">
                        Escalated
                      </span>
                    )}
                  </div>
                  <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-4 text-xs text-white/70">
                    <div>
                      <p className="text-[10px] uppercase tracking-wide text-white/50">User</p>
                      <p className="text-xs text-white">{interaction.userId || interaction.sessionId || "—"}</p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-wide text-white/50">Tokens</p>
                      <p className="text-xs text-white">
                        {(interaction.tokensPrompt || 0) + (interaction.tokensCompletion || 0)}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-wide text-white/50">Cost</p>
                      <p className="text-xs text-white">{formatMoney(interaction.tokensCost)}</p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-wide text-white/50">Latency</p>
                      <p className="text-xs text-white">{interaction.latencyMs ? `${interaction.latencyMs}ms` : "—"}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {interactions.length > 0 && (
          <div className="mt-5 flex justify-center gap-2">
            <button
              onClick={() => setFilters((prev) => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
              disabled={pagination.page <= 1}
              className="rounded-xl border border-white/15 bg-black/40 px-4 py-2 text-xs text-white hover:bg-white/10 disabled:opacity-50"
            >
              Previous
            </button>
            <span className="rounded-xl border border-white/15 bg-white/5 px-4 py-2 text-xs text-white">
              Page {pagination.page}
            </span>
            <button
              onClick={() =>
                setFilters((prev) => ({
                  ...prev,
                  page: pagination.page < pagination.pages ? pagination.page + 1 : pagination.page
                }))
              }
              disabled={pagination.page >= pagination.pages}
              className="rounded-xl border border-white/15 bg-black/40 px-4 py-2 text-xs text-white hover:bg-white/10 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}
      </section>
    </div>
  );
}
